<template>
  <div class="page-container">
    <el-card shadow="never" v-loading="loading">
      <!-- 头部区域 -->
      <div class="detail-header">
        <div class="header-left">
          <el-button icon="el-icon-arrow-left" size="small" @click="$router.back()">返回列表</el-button>
          <h3 class="header-title">订单详情 - {{ order.orderNo }}</h3>
          <el-tag :type="getStatusType(order.status)" size="small" style="margin-left: 12px;">
            {{ getStatusText(order.status) }}
          </el-tag>
        </div>
        <div class="header-actions">
          <!-- 查询类按钮 -->
          <div class="action-group">
            <el-button
              v-if="order.alipayOrderId"
              type="info"
              size="small"
              icon="el-icon-view"
              @click="handleAlipayQuery"
            >
              支付宝订单
            </el-button>
            <el-button
              type="warning"
              size="small"
              icon="el-icon-warning"
              @click="handleRisk"
            >
              风控
            </el-button>
            <el-button
              type="info"
              size="small"
              icon="el-icon-document"
              @click="showOrderLogs"
            >
              操作记录
            </el-button>
          </div>
          <!-- 操作类按钮 -->
          <div class="action-group">
            <el-button
              v-if="order.status === 'APPROVED'"
              type="primary"
              size="small"
              icon="el-icon-s-promotion"
              @click="openDeliverDialog"
            >
              发货
            </el-button>
            <el-tag
              v-if="order.status === 'WAIT_CONTRACT'"
              type="warning"
              size="medium"
              effect="plain"
              style="font-size: 14px; padding: 6px 16px; border-radius: 4px; border: 1px solid #E6A23C;"
            >
              <i class="el-icon-loading" style="margin-right: 6px;"></i>
              合同签署中，签署完成后自动进入待发货
            </el-tag>
            <el-button
              v-if="order.status === 'DELIVERED'"
              type="success"
              size="small"
              icon="el-icon-check"
              @click="handleReceive"
            >
              确认收货
            </el-button>
            <el-button
              v-if="canReturn(order.status)"
              type="warning"
              size="small"
              icon="el-icon-truck"
              @click="handleReturn"
            >
              用户寄回
            </el-button>
            <el-button
              v-if="canReceiveReturn(order.status)"
              type="primary"
              size="small"
              icon="el-icon-box"
              @click="handleReceiveReturn"
            >
              确认归还收货
            </el-button>
            <el-button
              v-if="canFinish(order.status)"
              type="success"
              size="small"
              icon="el-icon-circle-check"
              @click="handleFinish"
            >
              完成
            </el-button>


          </div>
        </div>
      </div>

      <el-divider />

      <!-- 订单信息 -->
      <div class="info-section">
        <h4 class="section-title">订单信息</h4>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="订单编号">{{ order.orderNo }}</el-descriptions-item>
          <el-descriptions-item label="订单类型">{{ order.orderType || '租赁订单' }}</el-descriptions-item>
          <el-descriptions-item label="订单标题">{{ order.title }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag :type="getStatusType(order.status)" size="mini">
              {{ getStatusText(order.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="签约状态">
            {{ order.signStatus || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="支付状态">
            <el-tag :type="order.payStatus === 'PAID' ? 'success' : 'warning'" size="mini">
              {{ order.payStatus === 'PAID' ? '已支付' : '待支付' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="配送方式">{{ formatDeliveryType(order.deliveryType) }}</el-descriptions-item>
          <el-descriptions-item label="快递公司">
            {{ getCompanyName(order.deliveryCompany) || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="快递单号">
            <span v-if="order.waybillId">
              {{ order.waybillId }}
              <el-button
                v-if="order.deliveryCompany && order.deliveryCompany !== 'OTHER'"
                link
                type="primary"
                size="mini"
                @click="openExpressTrack(order.deliveryCompany, order.waybillId)"
              >
                查物流
              </el-button>
            </span>
            <span v-else>-</span>
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ order.createdAt }}</el-descriptions-item>
          <el-descriptions-item label="更新时间">{{ order.updatedAt }}</el-descriptions-item>
          <el-descriptions-item v-if="order.memo" label="备注">{{ order.memo }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 商品信息 -->
      <div class="info-section" style="margin-top: 20px;">
        <h4 class="section-title">商品信息</h4>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="商品名称">{{ order.goodsName }}</el-descriptions-item>
          <el-descriptions-item label="商品ID">{{ order.goodsId }}</el-descriptions-item>
          <el-descriptions-item label="成色">{{ order.fineness || '-' }}</el-descriptions-item>
          <el-descriptions-item label="成色等级">{{ order.finenessGrade || '-' }}</el-descriptions-item>
          <el-descriptions-item label="租赁模式">{{ order.rentModel || '-' }}</el-descriptions-item>
          <el-descriptions-item label="商品价值">¥{{ order.itemValue }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 费用信息 -->
      <div class="info-section" style="margin-top: 20px;">
        <h4 class="section-title">费用信息</h4>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="日租金">¥{{ order.dailyRent }}</el-descriptions-item>
            <el-descriptions-item label="数量" v-if="order.quantity"><span style="font-size:12px;color:#909399;">x{{ order.quantity }}</span></el-descriptions-item>
          <el-descriptions-item label="租期天数">{{ order.rentDays }} 天</el-descriptions-item>
          <el-descriptions-item label="租期类型">
            <el-tag size="mini" :type="order.rentType === 'LONG' ? 'danger' : 'info'">
              {{ order.rentType === 'LONG' ? '长租' : (order.rentType === 'SHORT' ? '短租' : '-') }}
            </el-tag>
            <span v-if="order.rentUnit" style="margin-left: 8px; color: #909399; font-size: 12px;">
              计费单位: {{ rentUnitText(order.rentUnit) }} × {{ order.unitCount || 1 }}
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="订单金额">¥{{ order.orderPrice }}</el-descriptions-item>
          <el-descriptions-item label="押金">¥{{ order.depositPrice }}</el-descriptions-item>
          <el-descriptions-item label="运费">¥{{ order.freight || '0' }}</el-descriptions-item>
          <el-descriptions-item label="附加费用">¥{{ order.additionalPrice || '0' }}</el-descriptions-item>
          <el-descriptions-item label="租期开始">{{ order.rentStartTime || '-' }}</el-descriptions-item>
          <el-descriptions-item label="租期结束">{{ order.rentEndTime || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="order.finishType" label="完结类型">
            {{ finishTypeText(order.finishType) }}
          </el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 收货信息 -->
      <div class="info-section" style="margin-top: 20px;">
        <h4 class="section-title">收货信息</h4>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="收货人">{{ order.receiverName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ order.receiverPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="省份">{{ order.receiverProvince || '-' }}</el-descriptions-item>
          <el-descriptions-item label="城市">{{ order.receiverCity || '-' }}</el-descriptions-item>
          <el-descriptions-item label="区县">{{ order.receiverDistrict || '-' }}</el-descriptions-item>
          <el-descriptions-item label="详细地址" :span="2">{{ order.receiverAddress || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 归还信息 -->
      <div class="info-section" style="margin-top: 20px;">
        <h4 class="section-title">归还信息</h4>
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item label="归还人">{{ order.returnName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ order.returnPhone || '-' }}</el-descriptions-item>
          <el-descriptions-item label="归还地址" :span="2">{{ order.returnAddress || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 客户信息 -->
      <div class="info-section" style="margin-top: 20px;">
        <h4 class="section-title">客户信息</h4>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="客户姓名">{{ order.tenantName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="身份证号">{{ order.tenantIdCard || '-' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ order.tenantPhone || '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>

      <!-- 商品快照 -->
      <div class="info-section" style="margin-top: 20px;" v-if="snapshot">
        <h4 class="section-title">商品快照</h4>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="商品名称">{{ snapshot.goodsName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="商品分类">{{ snapshot.category || '-' }}</el-descriptions-item>
          <el-descriptions-item label="品牌">{{ snapshot.brand || '-' }}</el-descriptions-item>
          <el-descriptions-item label="成色">{{ snapshot.fineness || '-' }}</el-descriptions-item>
          <el-descriptions-item label="成色等级">{{ snapshot.finenessGrade || '-' }}</el-descriptions-item>
          <el-descriptions-item label="租赁模式">{{ snapshot.rentModel || '-' }}</el-descriptions-item>
          <el-descriptions-item label="商品价值">¥{{ snapshot.itemValue || '0' }}</el-descriptions-item>
          <el-descriptions-item label="日租金">¥{{ snapshot.dailyRent || '0' }}</el-descriptions-item>
              <el-descriptions-item label="数量" v-if="order.quantity"><span style="font-size:12px;color:#909399;">x{{ order.quantity }}</span></el-descriptions-item>
          <el-descriptions-item label="押金">¥{{ snapshot.depositPrice || '0' }}</el-descriptions-item>
          <el-descriptions-item label="运费">¥{{ snapshot.freight || '0' }}</el-descriptions-item>
          <el-descriptions-item label="增值服务费">¥{{ snapshot.additionalPrice || '0' }}</el-descriptions-item>
        </el-descriptions>
        <h4 class="section-title" style="margin-top: 16px;">租期快照</h4>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="租期方案">{{ snapshot.rentPeriodName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="租期天数">{{ snapshot.rentPeriodDays || '-' }} 天</el-descriptions-item>
          <el-descriptions-item label="租期日租金">¥{{ snapshot.rentPeriodDailyRent || '0' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-card>

    <!-- 合同记录卡片 -->
    <el-card shadow="never" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span style="font-weight: 600;">合同记录</span>
        <div style="float: right;">
          <el-button
            v-if="order.signStatus === 'SIGNED' && order.rentType !== 'LONG'"
            type="primary"
            size="small"
            icon="el-icon-document-copy"
            :loading="contractLoading"
            @click="handleCreateContract"
          >
            创建合同
          </el-button>
          <el-tag
            v-if="order.rentType === 'LONG' && order.status === 'WAIT_CONTRACT'"
            type="warning"
            size="small"
            effect="plain"
            style="margin-left: 4px;"
          >
            长租订单合同已自动创建，待签署
          </el-tag>
            <el-button
              type="success"
              size="small"
      <el-table :data="contracts" size="small" border stripe v-if="contracts && contracts.length">
        <el-table-column prop="contractNo" label="合同编号" width="200" show-overflow-tooltip />
        <el-table-column prop="contractTitle" label="合同标题" min-width="160" show-overflow-tooltip />
        <el-table-column label="合同状态" width="110" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="contractStatusTag(scope.row.contractStatus)">
              {{ contractStatusText(scope.row.contractStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="partyASignTime" label="甲方签署时间" width="170" align="center">
          <template slot-scope="scope">{{ scope.row.partyASignTime || '-' }}</template>
        </el-table-column>
        <el-table-column prop="partyBSignTime" label="乙方签署时间" width="170" align="center">
          <template slot-scope="scope">{{ scope.row.partyBSignTime || '-' }}</template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="170" align="center" />
        <el-table-column label="操作" width="160" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.contractStatus === 'SIGNED'"
              type="text"
              size="small"
              icon="el-icon-view"
              @click="handleDownloadContract(scope.row)"
            >
              查看
            </el-button>
            <el-button
              v-if="scope.row.contractStatus === 'WAIT_SIGN' || scope.row.contractStatus === 'SIGNING'"
              type="text"
              size="small"
              icon="el-icon-refresh"
              :loading="scope.row._refreshing"
              @click="handleRefreshSignUrl(scope.row)"
            >
              获取签署链接
            </el-button>
            <el-button
              v-if="scope.row.contractStatus === 'WAIT_SIGN' || scope.row.contractStatus === 'SIGNING'"
              type="text"
              size="small"
              icon="el-icon-upload2"
              :loading="scope.row._syncing"
              @click="handleSyncContractStatus(scope.row)"
              style="color: #67C23A;"
            >
              同步签署状态
            </el-button>
            <span v-else-if="scope.row.contractStatus !== 'SIGNED' && scope.row.contractStatus !== 'WAIT_SIGN' && scope.row.contractStatus !== 'SIGNING'" style="color: #c0c4cc; font-size: 12px;">-</span>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="暂无合同记录" :image-size="60" />
    </el-card>

    <!-- 租期明细卡片 -->
    <el-card shadow="never" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span style="font-weight: 600;">租期明细</span>
      </div>
      <el-table :data="installmentData" v-loading="installmentLoading" size="small" border stripe row-key="installmentNo">
        <el-table-column prop="installmentNo" label="期数" width="80" align="center">
          <template slot-scope="scope">
            <el-tag size="mini">第{{ scope.row.installmentNo }}期</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="installmentPrice" label="本期租金" width="130" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.installmentPrice }}
          </template>
        </el-table-column>
        <el-table-column prop="planPayTime" label="计划扣款日" width="180">
          <template slot-scope="scope">
            {{ formatTime(scope.row.planPayTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="instStatusTag(scope.row.status)">
              {{ instStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="paidAmount" label="已付金额" width="120" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.paidAmount || '0.00' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="280" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status !== 'PAID'"
              type="text"
              size="small"
              style="color: #E6A23C;"
              @click="handleDeductInstallment(scope.row)"
            >
              租金扣款
            </el-button>
            <el-button
              v-if="scope.row.status !== 'PAID'"
              type="text"
              size="small"
              style="color: #67C23A;"
              @click="handleOfflinePay(scope.row)"
            >
              线下付租
            </el-button>
            <el-button
              type="text"
              size="small"
              @click="showDeductionRecords(scope.row)"
            >
              扣款记录
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!installmentLoading && installmentData.length === 0" style="text-align: center; color: #909399; padding: 20px 0;">
        暂无分期数据
      </div>
    </el-card>

    <!-- 扣款记录弹窗 -->
    <el-dialog
      title="扣款记录"
      :visible.sync="deductionRecordsVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div v-if="currentInstallment" slot="title">
        扣款记录 - 第{{ currentInstallment.installmentNo }}期
      </div>
      <div v-loading="deductionRecordsLoading">
        <el-empty v-if="!deductionRecordsLoading && deductionRecords.length === 0" description="暂无扣款记录" :image-size="60" />
        <el-table v-else :data="deductionRecords" size="small" border stripe>
          <el-table-column prop="createdAt" label="扣款时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="扣款金额" width="120" align="center">
            <template slot-scope="scope">¥{{ scope.row.amount || '0.00' }}</template>
          </el-table-column>
          <el-table-column label="支付方式" width="120" align="center">
            <template slot-scope="scope">{{ StatusMap.getPayMethodText(scope.row.payMethod) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="mini">{{ StatusMap.getPaymentStatusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer">
        <el-button @click="deductionRecordsVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 线下付租弹窗 -->
    <el-dialog
      title="线下付租"
      :visible.sync="offlinePayVisible"
      width="450px"
      :close-on-click-modal="false"
    >
      <div slot="title" v-if="currentInstallment">
        线下付租 - 第{{ currentInstallment.installmentNo }}期
      </div>
      <el-form :model="offlinePayForm" label-width="80px">
        <el-form-item label="扣款金额">
          <el-input v-model="offlinePayForm.amount" placeholder="请输入金额" />
        </el-form-item>
        <el-form-item label="交易号">
          <el-input v-model="offlinePayForm.tradeNo" placeholder="请输入交易号" />
        </el-form-item>
        <el-form-item label="支付渠道">
          <el-select v-model="payChannelIndex" placeholder="请选择支付渠道" style="width: 100%;" @change="onPayChannelChange">
            <el-option v-for="(ch, idx) in payChannels" :key="idx" :label="ch" :value="idx" />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="offlinePayVisible = false">取消</el-button>
        <el-button type="primary" @click="submitOfflinePay">确认</el-button>
      </span>
    </el-dialog>

    <!-- 残值购买明细卡片 -->
    <el-card shadow="never" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span style="font-weight: 600;">残值购买明细</span>
        <el-button
          style="float: right;"
          v-if="order.signStatus === 'SIGNED' && canBuyout(order.status)"
          type="warning"
          size="small"
          icon="el-icon-shopping-cart-2"
          :loading="buyoutLoading"
          @click="handleBuyout"
        >
          发起残值购买
        </el-button>
      </div>
      <el-table :data="buyoutList" v-loading="buyoutListLoading" size="small" border stripe>
        <el-table-column prop="outBuyoutNo" label="商户单号" width="200" show-overflow-tooltip />
        <el-table-column prop="alipayBuyoutOrderNo" label="支付宝订单号" width="200" show-overflow-tooltip />
        <el-table-column prop="buyoutPrice" label="残值金额" width="120" align="center">
          <template slot-scope="scope">
            <span style="color: #E6A23C; font-weight: bold;">¥{{ scope.row.buyoutPrice || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="payStatus" label="支付状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="buyoutPayStatusTag(scope.row.payStatus)">
              {{ buyoutPayStatusText(scope.row.payStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="tradeNo" label="交易号" width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.tradeNo || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="paidAt" label="支付时间" width="170" align="center">
          <template slot-scope="scope">
            {{ scope.row.paidAt || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="170" align="center">
          <template slot-scope="scope">
            {{ scope.row.createdAt }}
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!buyoutListLoading && buyoutList.length === 0" style="text-align: center; color: #909399; padding: 20px 0;">
        暂无残值购买记录
      </div>
    </el-card>

    <!-- 买断弹窗 -->
    <el-dialog title="买断确认" :visible.sync="buyoutDialogVisible" width="520px" :close-on-click-modal="false">
      <el-descriptions :column="1" border size="small" v-if="buyoutInfo">
        <el-descriptions-item label="原订单号">{{ order.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="商品名称">{{ order.goodsName }}</el-descriptions-item>
        <el-descriptions-item label="商品价值">¥{{ order.itemValue || '0' }}</el-descriptions-item>
            <el-descriptions-item label="数量" v-if="order.quantity"><span style="font-size:12px;color:#909399;">x{{ order.quantity }}</span></el-descriptions-item>
        <el-descriptions-item label="买断金">
          <span style="color: #f56c6c; font-weight: 600; font-size: 16px;">¥{{ buyoutInfo.buyoutPrice || '0' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="买断单号">{{ buyoutInfo.outBuyoutNo || '-' }}</el-descriptions-item>
        <el-descriptions-item v-if="buyoutInfo.payStatus" label="支付状态">
          <el-tag size="mini" :type="buyoutInfo.payStatus === 'PAID' ? 'success' : 'warning'">
            {{ buyoutInfo.payStatus === 'PAID' ? '已支付' : '待支付' }}
          </el-tag>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="buyoutDialogVisible = false">取消</el-button>
        <el-button
          v-if="buyoutInfo && buyoutInfo.payStatus !== 'PAID'"
          type="warning"
          :loading="buyoutPayLoading"
          @click="handlePayBuyout"
        >
          确认支付买断金
        </el-button>
      </div>
    </el-dialog>

    <!-- 续租弹窗 -->
    <el-dialog title="续租" :visible.sync="renewDialogVisible" width="520px" :close-on-click-modal="false">
      <el-form :model="renewForm" label-width="100px" size="small">
        <el-form-item label="续租单位">
          <el-select v-model="renewForm.rentUnit" placeholder="请选择" style="width: 160px;">
            <el-option label="按天" value="DAY" />
            <el-option label="按周" value="WEEK" />
            <el-option label="按月" value="MONTH" />
          </el-select>
        </el-form-item>
        <el-form-item label="续租数量">
          <el-input-number v-model="renewForm.unitCount" :min="1" :max="36" />
        </el-form-item>
        <el-form-item label="单位租金">
          <el-input-number v-model="renewForm.unitRent" :min="0" :precision="2" :step="0.01" style="width: 180px;" />
          <span style="margin-left: 8px; color: #909399; font-size: 12px;">留空则沿用原订单租金</span>
        </el-form-item>
        <el-form-item>
          <el-alert type="info" :closable="false" show-icon>
            短租且距租期结束 ≤ 90 天走展期续租；长租或超窗口走新创续租订单
          </el-alert>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="renewDialogVisible = false">取消</el-button>
        <el-button type="success" :loading="renewLoading" @click="submitRenew">确认续租</el-button>
      </div>
    </el-dialog>

    <!-- 赔付记录卡片 -->
    <el-card shadow="never" style="margin-top: 20px;">
      <div slot="header" class="clearfix">
        <span style="font-weight: 600;">赔付记录</span>
        <el-button
          style="float: right;"
          v-if="canCompensation(order.status) && order.signStatus === 'SIGNED'"
          type="danger"
          size="small"
          icon="el-icon-warning"
          @click="openCompensationDialog"
        >
          申请赔付
        </el-button>
      </div>

      <el-table :data="compensationList" v-loading="compensationListLoading" size="small" border stripe>
        <el-table-column prop="aftersaleId" label="售后单号" width="170" show-overflow-tooltip />
        <el-table-column prop="compensationType" label="赔付类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="getCompensationTypeTag(scope.row.compensationType)" size="mini">
              {{ getCompensationTypeText(scope.row.compensationType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="compensationAmount" label="赔付金额" width="100" align="center">
          <template slot-scope="scope">
            <span style="color: #F56C6C;">¥{{ scope.row.compensationAmount || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="penaltyAmount" label="违约金" width="100" align="center">
          <template slot-scope="scope">
            <span style="color: #E6A23C;">¥{{ scope.row.penaltyAmount || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="totalAmount" label="总金额" width="100" align="center">
          <template slot-scope="scope">
            <span style="color: #409EFF; font-weight: bold;">¥{{ scope.row.totalAmount || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="aftersaleStatus" label="审核状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="getAftersaleStatusType(scope.row.aftersaleStatus)" size="mini">
              {{ getAftersaleStatusText(scope.row.aftersaleStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="payStatus" label="支付状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="getPayStatusType(scope.row.payStatus)" size="mini">
              {{ getPayStatusText(scope.row.payStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reason" label="赔付原因" min-width="150" show-overflow-tooltip />
        <el-table-column prop="createdAt" label="创建时间" width="160">
          <template slot-scope="scope">{{ logTime(scope.row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.aftersaleStatus === 'APPROVED' && (scope.row.payStatus === 'UNPAID' || scope.row.payStatus === 'FAILED')"
              type="text"
              size="small"
              style="color: #E6A23C;"
              @click="handleDeduct(scope.row)"
            >
              {{ scope.row.payStatus === 'FAILED' ? '重试扣款' : '发起扣款' }}
            </el-button>
            <el-button
              v-if="scope.row.aftersaleStatus !== 'CLOSED' && scope.row.payStatus !== 'PAID'"
              type="text"
              size="small"
              style="color: #909399;"
              @click="handleCloseCompensation(scope.row)"
            >
              关闭
            </el-button>
            <el-button type="text" size="small" @click="showCompensationDetail(scope.row)">
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!compensationListLoading && compensationList.length === 0" style="text-align: center; color: #909399; padding: 20px 0;">
        暂无赔付记录
      </div>
    </el-card>

    <!-- 赔付详情弹窗 -->
    <el-dialog title="赔付详情" :visible.sync="compensationDetailVisible" width="600px">
      <el-descriptions :column="2" border size="small" v-if="currentCompensation">
        <el-descriptions-item label="售后单号">{{ currentCompensation.aftersaleId }}</el-descriptions-item>
        <el-descriptions-item label="赔付类型">
          <el-tag :type="getCompensationTypeTag(currentCompensation.compensationType)" size="mini">
            {{ getCompensationTypeText(currentCompensation.compensationType) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="审核状态">
          <el-tag :type="getAftersaleStatusType(currentCompensation.aftersaleStatus)" size="mini">
            {{ getAftersaleStatusText(currentCompensation.aftersaleStatus) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="支付状态">
          <el-tag :type="getPayStatusType(currentCompensation.payStatus)" size="mini">
            {{ getPayStatusText(currentCompensation.payStatus) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="赔付金额">
          <span style="color: #F56C6C;">¥{{ currentCompensation.compensationAmount || '0.00' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="违约金">
          <span style="color: #E6A23C;">¥{{ currentCompensation.penaltyAmount || '0.00' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="总金额">
          <span style="color: #409EFF; font-weight: bold;">¥{{ currentCompensation.totalAmount || '0.00' }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ logTime(currentCompensation.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="赔付原因" :span="2">{{ currentCompensation.reason || '-' }}</el-descriptions-item>
        <el-descriptions-item label="详细描述" :span="2">{{ currentCompensation.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="证据图片" :span="2" v-if="currentCompensation.evidenceImages">
          <div style="display: flex; flex-wrap: wrap;">
            <el-image
              v-for="(img, index) in parseEvidenceImages(currentCompensation.evidenceImages)"
              :key="index"
              :src="img"
              :preview-src-list="parseEvidenceImages(currentCompensation.evidenceImages)"
              fit="cover"
              style="width: 80px; height: 80px; margin-right: 8px; margin-bottom: 8px;"
            />
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="审核时间" v-if="currentCompensation.reviewedAt">{{ logTime(currentCompensation.reviewedAt) }}</el-descriptions-item>
        <el-descriptions-item label="审核备注" v-if="currentCompensation.reviewRemark">{{ currentCompensation.reviewRemark }}</el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button @click="compensationDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="订单发货"
      :visible.sync="deliverDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      custom-class="deliver-dialog"
    >
      <el-form ref="deliverFormRef" :model="deliverForm" label-width="96px" label-position="right">
        <el-form-item label="发货方式" required>
          <el-radio-group v-model="deliverForm.deliveryType">
            <el-radio label="SELFPICK">
              <i class="el-icon-location-outline" />
              <span>到店自提</span>
            </el-radio>
            <el-radio label="EXPRESS">
              <i class="el-icon-s-promotion" />
              <span>快递发货</span>
            </el-radio>
            <el-radio label="INTRA_CITY_EXPRESS">
              <i class="el-icon-s-cooperation" />
              <span>同城配送</span>
            </el-radio>
          </el-radio-group>
        </el-form-item>

        <template v-if="deliverForm.deliveryType === 'EXPRESS'">
          <el-form-item label="快递公司" required>
            <el-select
              v-model="deliverForm.deliveryCompany"
              placeholder="请选择快递公司"
              filterable
              style="width: 100%"
            >
              <el-option
                v-for="c in expressCompanies"
                :key="c.id"
                :label="c.name"
                :value="c.id"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="快递单号" required>
            <el-input
              v-model="deliverForm.waybillId"
              placeholder="请输入快递单号"
              clearable
            />
          </el-form-item>
        </template>

        <template v-else-if="deliverForm.deliveryType === 'INTRA_CITY_EXPRESS'">
          <el-form-item label="配送说明">
            <el-input
              v-model="deliverForm.comment"
              type="textarea"
              :rows="2"
              placeholder="可填写配送备注（可选）"
            />
          </el-form-item>
        </template>

        <el-form-item v-if="deliverForm.deliveryType === 'SELFPICK'" label=" ">
          <el-alert
            title="用户到店自提，无需填写物流信息"
            type="info"
            show-icon
            :closable="false"
            style="width: 100%;"
          />
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="deliverDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="deliverLoading" @click="submitDeliver">
          确认发货
        </el-button>
      </span>
    </el-dialog>

    <!-- 支付宝订单查询结果弹窗 -->
    <el-dialog
      title="支付宝订单详情"
      :visible.sync="alipayDialogVisible"
      width="800px"
      top="5vh"
    >
      <div v-loading="alipayLoading">
        <template v-if="alipayOrderData">
          <el-descriptions :column="2" border size="small" style="margin-bottom: 16px;">
            <el-descriptions-item label="支付宝订单号">{{ alipayOrderData.alipayOrderId }}</el-descriptions-item>
            <el-descriptions-item label="商户订单号">{{ alipayOrderData.outOrderId }}</el-descriptions-item>
            <el-descriptions-item label="订单类型">
              <el-tag size="mini">{{ alipayOrderData.orderType }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="支付宝状态">
              <el-tag size="mini" :type="alipayStatusTag(alipayOrderData.status)">{{ alipayOrderData.status }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="订单标题">{{ alipayOrderData.title }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ alipayOrderData.orderCreateTime }}</el-descriptions-item>
            <el-descriptions-item label="本地订单状态">
              <el-tag size="mini">{{ alipayOrderData.localStatus }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="本地签约状态">
              <el-tag size="mini">{{ alipayOrderData.localSignStatus }}</el-tag>
            </el-descriptions-item>
          </el-descriptions>

          <!-- 金额信息 -->
          <div v-if="alipayOrderData.priceInfo" class="alipay-section">
            <h4 class="alipay-section-title">金额信息</h4>
            <el-descriptions :column="3" border size="small">
              <el-descriptions-item label="订单总价">
                ¥{{ alipayOrderData.priceInfo.order_price }}
              </el-descriptions-item>
              <el-descriptions-item label="押金">
                ¥{{ alipayOrderData.priceInfo.deposit_price || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="运费">
                ¥{{ alipayOrderData.priceInfo.freight || '0.00' }}
              </el-descriptions-item>
              <el-descriptions-item label="增值服务费" v-if="alipayOrderData.priceInfo.additional_price">
                ¥{{ alipayOrderData.priceInfo.additional_price }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 租期计划（分期信息） -->
          <div v-if="alipayOrderData.rentPlanInfo && alipayOrderData.rentPlanInfo.installments" class="alipay-section">
            <h4 class="alipay-section-title">租金分期计划</h4>
            <el-table :data="alipayOrderData.rentPlanInfo.installments" size="small" border stripe row-key="installment_no">
              <el-table-column prop="installment_no" label="期数" width="80" align="center">
                <template slot-scope="scope">
                  <el-tag size="mini">第{{ scope.row.installment_no }}期</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="installment_price" label="租金" width="130" align="center">
                <template slot-scope="scope">¥{{ scope.row.installment_price }}</template>
              </el-table-column>
              <el-table-column prop="plan_pay_time" label="计划扣款日" width="180" align="center" />
              <el-table-column prop="buyout_price" label="当期买断价" width="130" align="center">
                <template slot-scope="scope">
                  <span v-if="scope.row.buyout_price">¥{{ scope.row.buyout_price }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
            </el-table>
            <p v-if="alipayOrderData.rentPlanInfo.rent_start_time" style="color: #909399; font-size: 12px; margin-top: 8px;">
              租期: {{ alipayOrderData.rentPlanInfo.rent_start_time }} ~ {{ alipayOrderData.rentPlanInfo.rent_end_time }}
            </p>
          </div>

          <!-- 签约信息 -->
          <div v-if="alipayOrderData.rentSignInfo" class="alipay-section">
            <h4 class="alipay-section-title">签约信息</h4>
            <div v-if="alipayOrderData.rentSignInfo.rent_deduct_info">
              <el-tag size="small" :type="alipayOrderData.rentSignInfo.rent_deduct_info.sign_status === 'SIGNED' ? 'success' : 'warning'">
                代扣: {{ alipayOrderData.rentSignInfo.rent_deduct_info.sign_status }}
              </el-tag>
            </div>
            <div v-if="alipayOrderData.rentSignInfo.fund_auth_freeze_info" style="margin-top: 6px;">
              <el-tag size="small" :type="alipayOrderData.rentSignInfo.fund_auth_freeze_info.sign_status === 'SIGNED' ? 'success' : 'warning'">
                预授权: {{ alipayOrderData.rentSignInfo.fund_auth_freeze_info.sign_status }}
              </el-tag>
              <span v-if="alipayOrderData.rentSignInfo.fund_auth_freeze_info.auth_no" style="margin-left: 8px; font-size: 12px; color: #909399;">
                {{ alipayOrderData.rentSignInfo.fund_auth_freeze_info.auth_no }}
              </span>
            </div>
          </div>

          <!-- 商品信息 -->
          <div v-if="alipayOrderData.itemInfos && alipayOrderData.itemInfos.length" class="alipay-section">
            <h4 class="alipay-section-title">商品信息</h4>
            <el-table :data="alipayOrderData.itemInfos" size="small" border stripe row-key="out_item_id">
              <el-table-column prop="item_name" label="商品名称" min-width="150" />
              <el-table-column prop="item_category" label="类目" width="100" />
              <el-table-column prop="item_cnt" label="数量" width="60" align="center" />
              <el-table-column prop="sale_price" label="单价" width="90" align="center">
                <template slot-scope="scope">¥{{ scope.row.sale_price }}</template>
              </el-table-column>
              <el-table-column prop="item_value" label="总价值" width="90" align="center">
                <template slot-scope="scope">¥{{ scope.row.item_value }}</template>
              </el-table-column>
              <el-table-column prop="item_fineness" label="成色" width="80" align="center" />
              <el-table-column prop="rent_model" label="租赁模式" width="90" align="center" />
            </el-table>
          </div>
        </template>
        <div v-else-if="!alipayLoading" style="text-align: center; color: #909399; padding: 40px 0;">
          暂无数据
        </div>
      </div>

      <span slot="footer">
        <el-button @click="alipayDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="'操作记录 · ' + (order ? order.orderNo : '')" :visible.sync="logVisible" width="680px" :close-on-click-modal="true">
      <div v-if="logLoading" style="text-align: center; padding: 40px; color: #909399;">
        <i class="el-icon-loading"></i> 加载中...
      </div>
      <div v-else-if="!logHistory || logHistory.length === 0" style="text-align: center; padding: 40px; color: #909399;">
        暂无操作记录
      </div>
      <el-timeline v-else style="padding: 16px 8px;">
        <el-timeline-item
          v-for="(item, index) in logHistory"
          :key="item.id"
          :timestamp="logTime(item.createdAt)"
          :type="['CREATE','APPROVE','DELIVER','CONFIRM_RECEIVE','FINISH','COMPLETE','RISK_CONSULT','PAY_NOTIFY'].includes(item.action) ? 'success' : (['SEND_RETURN','RECEIVE_RETURN','DEDUCT','INIT_PAY','OFFLINE_PAY'].includes(item.action) ? 'warning' : (['CANCEL','SIGN','PAY_CLOSED'].includes(item.action) ? 'danger' : 'info'))"
          hollow
        >
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-weight: 600; color: #303133;">{{ item.actionDesc || item.action }}</span>
            <el-tag v-if="item.fromStatus" size="mini">{{ item.fromStatus }}</el-tag>
            <i v-if="item.fromStatus && item.toStatus" class="el-icon-arrow-right" style="color: #909399; font-size: 12px;"></i>
            <el-tag v-if="item.toStatus" size="mini">{{ item.toStatus }}</el-tag>
          </div>
          <div v-if="item.remark" style="color: #606266; font-size: 12px; margin-top: 4px;">{{ item.remark }}</div>
          <div style="color: #909399; font-size: 12px; margin-top: 2px;">
            操作人：{{ item.operator || '-' }}（{{ item.operatorRole || '-' }}）
          </div>
        </el-timeline-item>
      </el-timeline>
      <span slot="footer">
        <el-button @click="logVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog title="风控详情" :visible.sync="riskVisible" width="1080px" :close-on-click-modal="true">
      <div style="margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center;">
        <div style="color: #606266;">
          订单 <b style="color: #409EFF;">{{ order.orderNo || '' }}</b>
          <span v-if="riskHistory.length" style="color: #909399; margin-left: 8px;">（共 {{ riskHistory.length }} 条记录）</span>
        </div>
        <div style="display: flex; gap: 8px;">
          <el-button
            v-if="order.signStatus === 'SIGNED'"
            type="success"
            size="small"
            :loading="legalLoading"
            @click="doLegalConsult"
          >
            {{ legalLoading ? '查询中...' : '发起人法查询' }}
          </el-button>
          <el-button
            v-if="order.signStatus === 'SIGNED'"
            type="warning"
            size="small"
            :loading="creditLoading"
            @click="doCreditConsult"
          >
            {{ creditLoading ? '查询中...' : '发起征信查询' }}
          </el-button>
          <el-button
            v-if="order.signStatus === 'SIGNED'"
            size="small"
            :loading="zuxianzhiLoading"
            @click="doZuxianzhiConsult"
          >
            {{ zuxianzhiLoading ? '查询中...' : '发起租先知查询' }}
          </el-button>
          <el-button type="primary" size="small" :loading="riskLoading" @click="doRiskConsult">
            {{ riskLoading ? '咨询中...' : '+ 新增风控咨询' }}
          </el-button>
        </div>
      </div>

      <el-row :gutter="12">
        <el-col :span="12">
          <div style="border: 1px solid #EBEEF5; border-radius: 6px; padding: 12px;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #303133; border-left: 3px solid #409EFF; padding-left: 8px;">订单概要</div>
            <el-descriptions :column="1" size="small" border>
              <el-descriptions-item label="订单标题">{{ order.title || '-' }}</el-descriptions-item>
              <el-descriptions-item label="商品">{{ order.goodsName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="状态">{{ order.status || '-' }}</el-descriptions-item>
              <el-descriptions-item label="租金">¥{{ order.orderPrice || '0.00' }}</el-descriptions-item>
              <el-descriptions-item label="押金">¥{{ order.depositPrice || '0.00' }}</el-descriptions-item>
              <el-descriptions-item label="签约">{{ order.signStatus || '-' }} / {{ order.signType || '-' }}</el-descriptions-item>
              <el-descriptions-item label="支付宝订单号">
                <span v-if="order.alipayOrderId" style="font-size: 12px;">{{ order.alipayOrderId }}</span>
                <span v-else>-</span>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </el-col>
        <el-col :span="12">
          <div style="border: 1px solid #EBEEF5; border-radius: 6px; padding: 12px;">
            <div style="font-weight: 600; margin-bottom: 8px; color: #303133; border-left: 3px solid #67C23A; padding-left: 8px;">用户概要</div>
            <el-descriptions :column="1" size="small" border>
              <el-descriptions-item label="租户姓名">{{ order.tenantName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="租户手机号">{{ order.tenantPhone || '-' }}</el-descriptions-item>
              <el-descriptions-item label="租户身份证">
                <span v-if="order.tenantIdCard" style="font-size: 12px; letter-spacing: 0.5px;">{{ order.tenantIdCard }}</span>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="openId">
                <span v-if="order.alipayOpenId" style="font-size: 12px;">{{ order.alipayOpenId }}</span>
                <span v-else>-</span>
              </el-descriptions-item>
            </el-descriptions>
          </div>
        </el-col>
      </el-row>

      <div style="margin-top: 16px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #303133; border-left: 3px solid #E6A23C; padding-left: 8px;">风控详情（租安盾历史咨询）</div>
        <el-table :data="riskHistory" v-loading="riskLoading" size="small" border stripe row-key="id" max-height="320">
          <el-table-column label="时间" width="160">
            <template slot-scope="scope">{{ logTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column prop="consultType" label="类型" width="100">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.consultType === 'PRE_CHECK' ? 'warning' : (scope.row.consultType === 'IN_RENT' ? '' : 'success')">
                {{ scope.row.consultType === 'PRE_CHECK' ? '下单前' : (scope.row.consultType === 'IN_RENT' ? '租用中' : '完结后') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="triggerSource" label="触发" width="90">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.triggerSource === 'AUTO' ? 'info' : 'warning'">{{ scope.row.triggerSource === 'AUTO' ? '自动' : '手动' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="结果" min-width="300">
            <template slot-scope="scope">
              <div style="font-size: 12px;">
                <div><b>综合等级:</b> {{ getRawRisk(scope.row, 'comprehensive_risk_models.risk_level') || '-' }}</div>
                <div><b>在租数:</b> {{ getRawRisk(scope.row, 'concurrent_rent_count') || '0' }} 条</div>
                <div v-if="getRawRisk(scope.row, 'ship_goods_risk_models')"><b>发货建议:</b> {{ getRawRisk(scope.row, 'ship_goods_risk_models.ship_advice') || '-' }}</div>
                <div v-if="getRawRisk(scope.row, 'vertical_rent_risk_models.risk_level')"><b>专项风险:</b> {{ getRawRisk(scope.row, 'vertical_rent_risk_models.risk_level') }}</div>
                <div v-if="getRawRisk(scope.row, 'legal_risk_models.risk_level')"><b>人法风险:</b> {{ getRawRisk(scope.row, 'legal_risk_models.risk_level') }}</div>
              </div>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="riskHistory.length === 0 && !riskLoading" style="text-align: center; color: #909399; padding: 20px 0;">
          暂无风控咨询记录
        </div>
      </div>

      <div style="margin-top: 16px;">
        <div style="font-weight: 600; margin-bottom: 8px; color: #303133; border-left: 3px solid #E6A23C; padding-left: 8px;">租先知（易租云）大数据报告</div>
        <el-table :data="zxzHistory" size="small" border stripe row-key="id" max-height="320">
          <el-table-column label="时间" width="160">
            <template slot-scope="scope">{{ logTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="来源" width="90">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.triggerSource === 'AUTO' ? 'info' : 'warning'">{{ scope.row.triggerSource === 'AUTO' ? '自动' : '手动' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="报告版本" width="100">
            <template slot-scope="scope">{{ scope.row.reportType || '-' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="80">
            <template slot-scope="scope">
              <el-tag size="mini" :type="scope.row.zxzCode === '0' ? 'success' : 'danger'">
                {{ scope.row.zxzCode === '0' ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="request_id" width="200">
            <template slot-scope="scope">{{ scope.row.requestId || '-' }}</template>
          </el-table-column>
          <el-table-column label="计费" width="60">
            <template slot-scope="scope">{{ scope.row.billed === 'Y' ? '是' : '否' }}</template>
          </el-table-column>
          <el-table-column label="说明" min-width="200">
            <template slot-scope="scope">{{ scope.row.zxzMsg || scope.row.errorMsg || '-' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="120">
            <template slot-scope="scope">
              <el-button v-if="scope.row.previewUrl" type="text" size="small" @click="openZxzPreview(scope.row.previewUrl)">查看报告</el-button>
              <span v-else style="color: #909399; font-size: 12px;">无预览</span>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="zxzHistory.length === 0" style="text-align: center; color: #909399; padding: 20px 0;">
          暂无租先知查询记录
        </div>
      </div>
      <span slot="footer">
        <el-button @click="riskVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 赔付申请弹窗 -->
    <el-dialog title="申请赔付" :visible.sync="compensationDialogVisible" width="600px" :close-on-click-modal="false">
      <el-form :model="compensationForm" label-width="120px">
        <el-form-item label="赔付类型" required>
          <el-select v-model="compensationForm.compensationType" placeholder="请选择赔付类型" @change="onCompensationTypeChange">
            <el-option label="设备损坏" value="DAMAGE" />
            <el-option label="设备丢失" value="LOST" />
            <el-option label="违约金" value="PENALTY" />
            <el-option label="逾期费用" value="OVERDUE" />
            <el-option label="其他" value="OTHER" />
          </el-select>
        </el-form-item>
        <el-form-item label="赔付金额" required>
          <el-input-number v-model="compensationForm.compensationAmount" :min="0.01" :precision="2" :step="10" />
          <span style="margin-left: 8px; color: #909399;">元</span>
        </el-form-item>
        <el-form-item label="违约金金额">
          <el-input-number v-model="compensationForm.penaltyAmount" :min="0" :precision="2" :step="10" />
          <span style="margin-left: 8px; color: #909399;">元</span>
        </el-form-item>
        <el-form-item label="证据图片">
          <el-upload
            action="/api/admin/upload"
            list-type="picture-card"
            :file-list="compensationForm.evidenceImages"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :before-upload="beforeUpload"
            :limit="9"
          >
            <i class="el-icon-plus"></i>
            <div slot="tip" class="el-upload__tip" style="color: #999; font-size: 12px; margin-top: 4px;">
              只能上传图片文件，单张不超过 5MB，最多9张
            </div>
          </el-upload>
        </el-form-item>
        <el-form-item label="赔付说明" required>
          <el-input type="textarea" v-model="compensationForm.description" :rows="3" placeholder="请详细说明赔付原因" />
        </el-form-item>
        <el-form-item label="申请原因" required>
          <el-input type="textarea" v-model="compensationForm.reason" :rows="2" placeholder="请输入申请原因" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="compensationDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="compensationLoading" @click="submitCompensation">提交</el-button>
      </span>
    </el-dialog>
  </div>
  <!-- e签宝同步结果弹窗 -->
  </el-dialog>
</template>

<script>
import { getOrderDetail, deliverOrder, receiveOrder, getOrderInstallments, completeOrder, sendReturnOrder, receiveReturnOrder, alipayQueryOrder, riskConsult, legalConsult, creditConsult, zuxianzhiConsult, createContract, listContracts, listRiskConsult, listOrderLogs, listZxzReports, createCompensation, getCompensationList, deductCompensation, closeCompensation, createBuyout, payBuyout, getBuyout, getBuyoutList, renewOrder, downloadContract, deductInstallment, getDeductionRecords, offlinePay, refreshContractSignUrl, syncContractSignStatus } from '@/api/order'
import { orderStatusMap, orderStatusTypeMap, getInstallmentStatusText, getInstallmentStatusTag } from '@common/statusMap.js'

const EXPRESS_COMPANIES = [
  { id: 'SF', name: '顺丰速运' },
  { id: 'YTO', name: '圆通速递' },
  { id: 'ZTO', name: '中通快递' },
  { id: 'STO', name: '申通快递' },
  { id: 'YD', name: '韵达速递' },
  { id: 'JD', name: '京东物流' },
  { id: 'EMS', name: '邮政速递 EMS' },
  { id: 'DBL', name: '德邦快递' },
  { id: 'TTKD', name: '天天快递' },
  { id: 'ZT', name: '宅急送' },
  { id: 'OTHER', name: '其他' }
]

const COMPANY_NAME_MAP = {}
EXPRESS_COMPANIES.forEach(c => { COMPANY_NAME_MAP[c.id] = c.name })

export default {
  name: 'OrderDetail',
  data() {
    return {
      loading: false,
      order: {},
      snapshot: null,
      deliverDialogVisible: false,
      deliverLoading: false,
      deliverForm: {
        deliveryType: 'SELFPICK',
        deliveryCompany: '',
        waybillId: '',
        comment: ''
      },
      expressCompanies: EXPRESS_COMPANIES,
      installmentData: [],
      installmentLoaded: false,
      installmentLoading: false,
      alipayDialogVisible: false,
      alipayLoading: false,
      alipayOrderData: null,
      logVisible: false,
      logLoading: false,
      logHistory: [],
      riskVisible: false,
      riskLoading: false,
      riskHistory: [],
      legalLoading: false,
      creditLoading: false,
      zuxianzhiLoading: false,
      zxzHistory: [],
      contractLoading: false,
      contracts: [],
      // 买断相关
      buyoutLoading: false,
      buyoutPayLoading: false,
      buyoutDialogVisible: false,
      buyoutInfo: null,
      buyoutList: [],
      buyoutListLoading: false,
      // 续租相关
      renewDialogVisible: false,
      renewLoading: false,
      renewForm: {
        rentUnit: 'DAY',
        unitCount: 1,
        unitRent: null
      },
      // 赔付相关
      compensationDialogVisible: false,
      compensationLoading: false,
      compensationForm: {
        compensationType: 'DAMAGE',
        compensationAmount: 0,
        penaltyAmount: 0,
        evidenceImages: [],
        description: '',
        reason: ''
      },
      compensationList: [],
      compensationListLoading: false,
      compensationDetailVisible: false,
      currentCompensation: null,
      // 扣款记录相关
      deductionRecordsVisible: false,
      deductionRecordsLoading: false,
      deductionRecords: [],
      currentInstallment: null,
      // 线下支付相关
      offlinePayVisible: false,
      offlinePayForm: {
        amount: '',
        tradeNo: '',
        payChannel: 'OTHER'
      },
      payChannels: ['微信', '支付宝', '现金', '其他'],
      payChannelIndex: 3
    }
  },
  created() {
    this.loadDetail()
  },
  methods: {
    async loadDetail() {
      this.loading = true
      try {
        const id = this.$route.params.id
        const res = await getOrderDetail(id)
        if (res.data) {
          this.order = res.data.order || {}
          this.snapshot = res.data.snapshot || null
        }
        // 加载分期列表
        await this.loadInstallments(id)
        // 加载赔付列表
        this.loadCompensationList(id)
        // 加载合同列表
        this.loadContracts()
        // 加载残值购买列表
        this.loadBuyoutList(id)
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadBuyoutList(orderId) {
      this.buyoutListLoading = true
      try {
        const res = await getBuyoutList(orderId)
        if (res.data) {
          this.buyoutList = res.data || []
        }
      } catch (e) {
        this.buyoutList = []
      } finally {
        this.buyoutListLoading = false
      }
    },
    async loadCompensationList(orderId) {
      this.compensationListLoading = true
      try {
        const res = await getCompensationList({ orderId: orderId, size: 100 })
        if (res.data) {
          this.compensationList = res.data.records || []
        }
      } catch (e) {
        this.compensationList = []
      } finally {
        this.compensationListLoading = false
      }
    },
    async loadInstallments(orderId) {
      this.installmentLoading = true
      try {
        const res = await getOrderInstallments(orderId)
        this.installmentData = (res.data || res || []).map(item => ({
          ...item,
          _deducting: false
        }))
      } catch (e) {
        this.installmentData = []
      } finally {
        this.installmentLoaded = true; this.installmentLoading = false
      }
    },
    getStatusType(status) {
      return orderStatusTypeMap[status] || 'info'
    },
    getStatusText(status) {
      return orderStatusMap[status] || status
    },
    esignStatusTag(status) {
      const m = { DRAFT: 'info', WAIT_SIGN: 'warning', SIGNING: 'warning', SIGNED: 'success', CANCELLED: 'danger', EXPIRED: 'info', FAILED: 'danger' }
      return m[status] || 'info'
    },
    esignFlowTag(flowStatus) {
      const m = { '0': 'info', '1': 'warning', '2': 'success', '3': 'danger', '4': 'danger', '5': 'info' }
      return m[flowStatus] || 'info'
    },
    esignFlowText(flowStatus) {
      const m = { '0': '未签署', '1': '签署中', '2': '已完成', '3': '已撤销', '4': '拒签', '5': '已过期' }
      return m[flowStatus] || ('状态:' + flowStatus)
    },
    formatDeliveryType(type) {
      const map = {
        SELFPICK: '到店自提',
        EXPRESS: '快递发货',
        INTRA_CITY_EXPRESS: '同城配送'
      }
      return map[type] || type || '-'
    },
    getCompanyName(id) {
      if (!id) return ''
      return COMPANY_NAME_MAP[id] || id
    },
    openDeliverDialog() {
      this.deliverForm = {
        deliveryType: 'SELFPICK',
        deliveryCompany: '',
        waybillId: '',
        comment: ''
      }
      this.deliverDialogVisible = true
    },
    async submitDeliver() {
      const f = this.deliverForm

      if (!f.deliveryType) {
        this.$message.error('请选择发货方式')
        return
      }

      if (f.deliveryType === 'EXPRESS') {
        if (!f.deliveryCompany) {
          this.$message.error('请选择快递公司')
          return
        }
        if (!f.waybillId || !f.waybillId.trim()) {
          this.$message.error('请输入快递单号')
          return
        }
      }

      this.deliverLoading = true
      try {
        await deliverOrder(this.order.id, {
          deliveryType: f.deliveryType,
          deliveryCompany: f.deliveryType === 'EXPRESS' ? f.deliveryCompany : undefined,
          waybillId: f.deliveryType === 'EXPRESS' ? f.waybillId.trim() : undefined
        })
        this.$message.success('发货成功')
        this.deliverDialogVisible = false
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '发货失败')
      } finally {
        this.deliverLoading = false
      }
    },
    async handleReceive() {
      try {
        await this.$confirm('确认该订单已收货？', '确认收货', { type: 'warning' })
      } catch (_) {
        return
      }
      try {
        await receiveOrder(this.order.id)
        this.$message.success('收货成功')
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '收货失败')
      }
    },
    openExpressTrack(companyId, waybillId) {
      const url = `https://www.kuaidi100.com/company/list?com=${companyId}&num=${encodeURIComponent(waybillId)}`
      window.open(url, '_blank')
    },
    formatTime(time) {
      if (!time) return '-'
      return time.replace('T', ' ').substring(0, 19)
    },
    instStatusTag(status) {
      return getInstallmentStatusTag(status)
    },
    instStatusText(status) {
      return getInstallmentStatusText(status)
    },
    // 判断是否可以显示归还/完成按钮
    canReturn(status) {
      if (!['RECEIVED', 'RENTING'].includes(status)) return false
      if (!this.installmentLoaded) return false
      if (this.installmentData.length === 0) return true
      return !this.installmentData.some(inst => inst.status === 'UNPAID' || inst.status === 'INIT')
    },
    canReceiveReturn(status) {
      return status === 'RETURN_DELIVERED'
    },
    canFinish(status) {
      return status === 'RETURN_RECEIVED'
    },
    async handleReturn() {
      try {
        await this.$confirm('确认用户已寄回商品？系统将调用支付宝完成用户寄回履约。', '用户寄回', { type: 'info' })
      } catch (_) {
        return
      }
      try {
        await sendReturnOrder(this.order.id)
        this.$message.success('用户寄回成功')
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '操作失败')
      }
    },
    async handleReceiveReturn() {
      try {
        await this.$confirm('确认已收到用户归还的商品？系统将调用支付宝完成商家收货履约。', '确认归还收货', { type: 'warning' })
      } catch (_) {
        return
      }
      try {
        await receiveReturnOrder(this.order.id)
        this.$message.success('归还收货成功')
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '操作失败')
      }
    },
    async handleFinish() {
      try {
        await this.$confirm('确认完结该订单？请确保所有租金已支付完毕。', '完成订单', { type: 'warning', confirmButtonText: '确认完成' })
      } catch (_) {
        return
      }
      try {
        await completeOrder(this.order.id)
        this.$message.success('订单已完成')
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '完成订单失败')
      }
    },
    async handleAlipayQuery() {
      this.alipayDialogVisible = true
      this.alipayLoading = true
      this.alipayOrderData = null
      try {
        const res = await alipayQueryOrder(this.order.id)
        this.alipayOrderData = res.data || res
      } catch (e) {
        this.$message.error(e.message || '查询支付宝订单失败')
      } finally {
        this.alipayLoading = false
      }
    },
    async showOrderLogs() {
      this.logHistory = []
      this.logVisible = true
      this.logLoading = true
      try {
        const res = await listOrderLogs(this.order.id)
        const list = (res && res.data) || []
        this.logHistory = list.slice().sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0
          return tb - ta
        })
      } catch (e) {
        this.$message.error(e.message || '加载操作记录失败')
      } finally {
        this.logLoading = false
      }
    },
    logTime(t) {
      if (!t) return '-'
      return String(t).replace('T', ' ').replace(/\..*$/, '')
    },
    async handleRisk() {
      this.riskVisible = true
      this.riskLoading = true
      this.riskHistory = []
      this.zxzHistory = []
      try {
        const res = await listRiskConsult(this.order.id)
        this.riskHistory = (res && res.data) || []
        const zxrRes = await listZxzReports(this.order.id)
        this.zxzHistory = (zxrRes && zxrRes.data) || []
      } catch (e) {
        this.$message.error(e.message || '加载风控记录失败')
      } finally {
        this.riskLoading = false
      }
    },
    async doRiskConsult() {
      if (!this.order || !this.order.id) return
      this.riskLoading = true
      try {
        const res = await riskConsult(this.order.id)
        this.zxzHistory.unshift(res.data || res)
        this.$message.success('风控咨询成功')
      } catch (e) {
        this.$message.error(e.message || '风控咨询失败')
      } finally {
        this.riskLoading = false
      }
    },
    async doLegalConsult() {
      if (!this.order || !this.order.id) return
      this.legalLoading = true
      try {
        const res = await legalConsult(this.order.id)
        this.zxzHistory.unshift(res.data || res)
        this.$message.success('人法查询成功')
      } catch (e) {
        this.$message.error(e.message || '人法查询失败')
      } finally {
        this.legalLoading = false
      }
    },
    async doCreditConsult() {
      if (!this.order || !this.order.id) return
      this.creditLoading = true
      try {
        const res = await creditConsult(this.order.id)
        this.zxzHistory.unshift(res.data || res)
        this.$message.success('征信查询成功')
      } catch (e) {
        this.$message.error(e.message || '征信查询失败')
      } finally {
        this.creditLoading = false
      }
    },
    async doZuxianzhiConsult() {
      if (!this.order || !this.order.id) return
      this.zuxianzhiLoading = true
      try {
        const res = await zuxianzhiConsult(this.order.id)
        this.zxzHistory.unshift(res.data || res)
        this.$message.success('租先知查询成功')
      } catch (e) {
        this.$message.error(e.message || '租先知查询失败')
      } finally {
        this.zuxianzhiLoading = false
      }
    },
    async handleCreateContract() {
      if (!this.order || !this.order.id) return
      this.contractLoading = true
      try {
        const res = await createContract(this.order.id)
        const contract = res.data || res
        this.$message.success('合同创建成功：' + (contract.contractNo || ''))
        this.loadContracts()
      } catch (e) {
        this.$message.error(e.message || '创建合同失败')
      } finally {
        this.contractLoading = false
      }
    },
    async loadContracts() {
      if (!this.order || !this.order.id) return
      try {
        const res = await listContracts(this.order.id)
        this.contracts = (res && res.data) || []
      } catch (e) {
        // ignore
      }
    },
    alipayStatusTag(status) {
      if (['CREATED', 'SIGNED', 'APPROVED', 'RECEIVED'].includes(status)) return 'success'
      if (['FINISHED'].includes(status)) return 'info'
      return 'warning'
    },
    getRawRisk(row, path) {
      if (!row || !row.rawResponse || !path) return null
      try {
        const data = typeof row.rawResponse === 'string' ? JSON.parse(row.rawResponse) : row.rawResponse
        const keys = path.split('.')
        let value = data
        for (const key of keys) {
          if (value == null) return null
          value = value[key]
        }
        return value
      } catch (e) {
        return null
      }
    },
    // 判断是否可申请赔付
    canCompensation(status) {
      return ['RECEIVED', 'RENTING', 'RETURN_DELIVERED', 'RETURN_RECEIVED'].includes(status)
    },
    // 买断：租用中或归还流程中
    canBuyout(status) {
      return ['RECEIVED', 'RENTING', 'RETURN_DELIVERED', 'RETURN_RECEIVED'].includes(status)
    },
    // 续租：租用中
    canRenew(status) {
      return ['RECEIVED', 'RENTING', 'RETURN_DELIVERED', 'RETURN_RECEIVED'].includes(status)
    },
    // 是否已有已签署合同
    hasSignedContract() {
      return this.contracts && this.contracts.some(c => c.contractStatus === 'SIGNED')
    },
    rentUnitText(unit) {
      const m = { DAY: '天', WEEK: '周', MONTH: '月' }
      return m[unit] || unit || '-'
    },
    finishTypeText(type) {
      const m = { NORMAL: '正常完结', BUYOUT: '买断完结', RENEWED: '续租完结' }
      return m[type] || type
    },
    contractStatusTag(status) {
      const m = { DRAFT: 'info', WAIT_SIGN: 'warning', SIGNED: 'success', EXPIRED: 'info', CANCELLED: 'danger' }
      return m[status] || 'info'
    },
    contractStatusText(status) {
      const m = { DRAFT: '草稿', WAIT_SIGN: '待签署', SIGNED: '已签署', EXPIRED: '已过期', CANCELLED: '已取消' }
      return m[status] || status
    },
    buyoutPayStatusTag(status) {
      const m = { UNPAID: 'warning', PAID: 'success', CLOSED: 'info' }
      return m[status] || 'info'
    },
    buyoutPayStatusText(status) {
      const m = { UNPAID: '待支付', PAID: '已支付', CLOSED: '已关闭' }
      return m[status] || status || '-'
    },
    // 买断：创建买断订单
    async handleBuyout() {
      if (!this.order || !this.order.id) return
      try {
        await this.$confirm('确定要买断该订单吗？买断后将不再继续租赁。', '买断确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        return
      }
      this.buyoutLoading = true
      try {
        await createBuyout(this.order.id)
        const res = await getBuyout(this.order.id)
        this.buyoutInfo = (res && res.data) || null
        this.buyoutDialogVisible = true
      } catch (e) {
        this.$message.error(e.message || '发起买断失败')
      } finally {
        this.buyoutLoading = false
      }
    },
    // 买断：支付买断金
    async handlePayBuyout() {
      if (!this.buyoutInfo || !this.buyoutInfo.id) return
      try {
        await this.$confirm('确定要发起买断支付吗？交易创建后，用户需在支付宝小程序完成支付。', '支付确认', {
          confirmButtonText: '发起支付',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch {
        return
      }
      this.buyoutPayLoading = true
      try {
        await payBuyout(this.buyoutInfo.id)
        this.$message.success('买断交易已创建，请用户在支付宝小程序完成支付')
        this.buyoutDialogVisible = false
        this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '支付买断金失败')
      } finally {
        this.buyoutPayLoading = false
      }
    },
    // 续租：打开弹窗
    openRenewDialog() {
      this.renewForm = {
        rentUnit: this.order.rentUnit || 'DAY',
        unitCount: 1,
        unitRent: this.order.unitRent || this.order.dailyRent || null
      }
      this.renewDialogVisible = true
    },
    // 续租：提交
    async submitRenew() {
      if (!this.order || !this.order.id) return
      if (!this.renewForm.unitCount || this.renewForm.unitCount <= 0) {
        this.$message.error('续租数量必须大于 0')
        return
      }
      this.renewLoading = true
      try {
        await renewOrder(this.order.id, {
          rentUnit: this.renewForm.rentUnit,
          unitCount: this.renewForm.unitCount,
          unitRent: this.renewForm.unitRent
        })
        this.$message.success('续租成功')
        this.renewDialogVisible = false
        this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '续租失败')
      } finally {
        this.renewLoading = false
      }
    },
    // 合同下载
    async handleDownloadContract(contract) {
      if (!contract || !contract.id) return
      // 优先用已存档 URL 直连
      if (contract.contractFileUrl) {
        window.open(contract.contractFileUrl, '_blank')
        return
      }
      // 未存档，打开后端接口直接预览（后端返回 inline PDF）
      const baseUrl = process.env.VUE_APP_API_BASE_URL || '/api/admin'
      window.open(baseUrl + '/contract/' + contract.id + '/download', '_blank')
    },
    async handleRefreshSignUrl(contract) {
      if (!contract || !contract.id) return
      this.$set(contract, '_refreshing', true)
      try {
        const res = await refreshContractSignUrl(this.orderId, contract.id)
        const updated = res.data || res
        Object.assign(contract, updated)
        this.$message.success('签署链接已重新获取，请在支付宝小程序中查看')
      } catch (e) {
        this.$message.error(e.message || '重新获取签署链接失败')
      } finally {
        if (contract) {
          this.$set(contract, '_refreshing', false)
        }
      }
    },
    async handleSyncContractStatus(contract) {
      if (!contract || !contract.id) return
      this.$set(contract, '_syncing', true)
      try {
        const res = await syncContractSignStatus(this.orderId, contract.id)
        this.$message.success('合同签署状态已同步')
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '同步签署状态失败')
      } finally {
        if (contract) {
          this.$set(contract, '_syncing', false)
        }
      }
    },
    handleCompensation() {
      this.compensationForm = {
        compensationType: '',
        description: '',
        reason: ''
      }
      this.onCompensationTypeChange()
      this.compensationDialogVisible = true
    },
    // 赔付类型变化时自动补全说明与原因
    onCompensationTypeChange() {
      const typeMap = {
        DAMAGE: { reason: '设备损坏', description: '租赁设备在使用过程中发生损坏，需要申请赔付维修或更换费用。' },
        LOST: { reason: '设备丢失', description: '租赁设备丢失，无法归还，需要申请赔付设备价值。' },
        PENALTY: { reason: '违约金', description: '用户违反租赁合同约定，需要申请违约金赔付。' },
        OVERDUE: { reason: '逾期费用', description: '租赁设备逾期未还，需要申请逾期费用赔付。' },
        OTHER: { reason: '其他', description: '其他原因需要申请赔付。' }
      }
      const item = typeMap[this.compensationForm.compensationType]
      if (item) {
        this.compensationForm.reason = item.reason
        this.compensationForm.description = item.description
      }
    },
    // 提交赔付申请
    async submitCompensation() {
      const form = this.compensationForm
      
      if (!form.compensationType) {
        this.$message.error('请选择赔付类型')
        return
      }
      
      if (!form.compensationAmount || form.compensationAmount <= 0) {
        this.$message.error('赔付金额必须大于0')
        return
      }
      
      if (!form.description || form.description.trim() === '') {
        this.$message.error('请输入赔付说明')
        return
      }
      
      if (!form.reason || form.reason.trim() === '') {
        this.$message.error('请输入申请原因')
        return
      }
      
      this.compensationLoading = true
      try {
        await createCompensation({
          orderId: this.order.id,
          compensationType: form.compensationType,
          compensationAmount: form.compensationAmount,
          penaltyAmount: form.penaltyAmount || 0,
          evidenceImages: form.evidenceImages.map(file => file.url || file.response?.data),
          description: form.description.trim(),
          reason: form.reason.trim()
        })
        this.$message.success('赔付申请提交成功')
        this.compensationDialogVisible = false
        await this.loadDetail()
      } catch (e) {
        this.$message.error(e.message || '赔付申请失败')
      } finally {
        this.compensationLoading = false
      }
    },
    // 图片上传成功回调
    handleUploadSuccess(response, file, fileList) {
      if (response && response.data) {
        file.url = response.data
      }
      this.compensationForm.evidenceImages = fileList
    },
    // 图片移除回调
    handleUploadRemove(file, fileList) {
      this.compensationForm.evidenceImages = fileList
    },
    // 上传前校验
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isImage) {
        this.$message.error('只能上传图片文件')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return false
      }
      return true
    },
    // 发起扣款
    async handleDeduct(row) {
      try {
        await this.$confirm('确认发起扣款吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deductCompensation(row.id)
        this.$message.success('扣款发起成功')
        await this.loadCompensationList(this.order.id)
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '扣款失败')
        }
      }
    },
    // 关闭/撤销售后单
    async handleCloseCompensation(row) {
      try {
        await this.$confirm('确认关闭该售后单吗？将同步撤销支付宝侧的售后单。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await closeCompensation(row.id)
        this.$message.success('售后单已关闭')
        await this.loadCompensationList(this.order.id)
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '关闭失败')
        }
      }
    },
    // 显示赔付详情
    showCompensationDetail(row) {
      this.currentCompensation = row
      this.compensationDetailVisible = true
    },
    // 赔付类型显示
    getCompensationTypeText(type) {
      const map = {
        DAMAGE: '设备损坏',
        LOST: '设备丢失',
        PENALTY: '违约金',
        OVERDUE: '逾期费用',
        OTHER: '其他'
      }
      return map[type] || type || '-'
    },
    getCompensationTypeTag(type) {
      const map = {
        DAMAGE: 'warning',
        LOST: 'danger',
        PENALTY: 'info',
        OVERDUE: 'warning',
        OTHER: 'info'
      }
      return map[type] || 'info'
    },
    // 售后状态显示
    getAftersaleStatusText(status) {
      const map = {
        APPROVED: '处理中',
        CLOSED: '已关闭'
      }
      return map[status] || status || '-'
    },
    getAftersaleStatusType(status) {
      const map = {
        APPROVED: 'success',
        CLOSED: 'info'
      }
      return map[status] || 'info'
    },
    // 支付状态显示
    getPayStatusText(status) {
      const map = {
        UNPAID: '待支付',
        PAID: '已支付',
        FAILED: '支付失败'
      }
      return map[status] || status || '-'
    },
    getPayStatusType(status) {
      const map = {
        UNPAID: 'warning',
        PAID: 'success',
        FAILED: 'danger'
      }
      return map[status] || 'info'
    },
    // 解析证据图片
    parseEvidenceImages(images) {
      if (!images) return []
      try {
        const parsed = typeof images === 'string' ? JSON.parse(images) : images
        return Array.isArray(parsed) ? parsed : []
      } catch (e) {
        return []
      }
    },
    // 分期扣款
    async handleDeductInstallment(inst) {
      try {
        await this.$confirm(`确认对第${inst.installmentNo}期发起租金扣款 (¥${inst.installmentPrice})？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deductInstallment(this.order.id, {
          installmentNo: inst.installmentNo,
          amount: inst.installmentPrice
        })
        this.$message.success('扣款请求已发起')
        await this.loadInstallments(this.order.id)
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error(e.message || '扣款失败')
        }
      }
    },
    // 线下付租
    handleOfflinePay(inst) {
      this.currentInstallment = inst
      this.offlinePayForm = {
        amount: inst.installmentPrice,
        tradeNo: '',
        payChannel: 'OTHER'
      }
      this.payChannelIndex = 3
      this.offlinePayVisible = true
    },
    onPayChannelChange(index) {
      this.payChannelIndex = index
      const channelMap = {
        '微信': 'WECHAT',
        '支付宝': 'ALIPAY',
        '现金': 'CASH',
        '其他': 'OTHER'
      }
      this.offlinePayForm.payChannel = channelMap[this.payChannels[index]] || 'OTHER'
    },
    async submitOfflinePay() {
      if (!this.offlinePayForm.amount || !this.offlinePayForm.tradeNo) {
        this.$message.warning('请填写完整信息')
        return
      }
      try {
        await offlinePay(this.order.id, {
          installmentNo: this.currentInstallment.installmentNo,
          amount: this.offlinePayForm.amount,
          tradeNo: this.offlinePayForm.tradeNo,
          payChannel: this.offlinePayForm.payChannel
        })
        this.$message.success('线下支付记录成功')
        this.offlinePayVisible = false
        await this.loadInstallments(this.order.id)
      } catch (e) {
        this.$message.error(e.message || '操作失败')
      }
    },
    // 扣款记录
    async showDeductionRecords(inst) {
      this.currentInstallment = inst
      this.deductionRecordsVisible = true
      this.deductionRecordsLoading = true
      this.deductionRecords = []
      try {
        const res = await getDeductionRecords(this.order.id, inst.installmentNo)
        this.deductionRecords = res.data || []
      } catch (e) {
        this.$message.error(e.message || '加载扣款记录失败')
      } finally {
        this.deductionRecordsLoading = false
      }
    },
    openZxzPreview(url) {
      if (!url) return
      window.open(url, '_blank')
    },

  }
}
</script>

<style scoped>
.page-container {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f1f1f;
  margin: 0;
  margin-left: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.header-actions .action-group .el-button {
  border-radius: 4px;
  font-weight: 500;
}

.header-actions .action-group .el-button--primary {
  background: #1677FF;
  border-color: #1677FF;
}

.header-actions .action-group .el-button--success {
  background: #00B42A;
  border-color: #00B42A;
}

.header-actions .action-group .el-button--warning {
  background: #FAAD14;
  border-color: #FAAD14;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f1f1f;
  margin-bottom: 12px;
}

.deliver-form {
  padding: 4px 0;
}

.delivery-type-row {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.row-label {
  font-size: 14px;
  color: #606266;
  margin-right: 16px;
  flex-shrink: 0;
}

.delivery-fields {
  padding-left: 8px;
}

.delivery-fields /deep/ .el-form-item {
  margin-bottom: 18px;
}

/* 支付宝订单弹窗样式 */
.alipay-section {
  margin-top: 20px;
}

.alipay-section-title {
  font-size: 14px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 10px;
  padding-left: 8px;
  border-left: 3px solid #409EFF;
}
</style>
